import React, { useState } from "react";
import { CheckCircle2, ArrowRight, Mail, Phone } from "lucide-react";

export default function DemoPage() {
  const [form, setForm] = useState({ name: "", role: "Doctor", org: "", email: "", phone: "", patients: "1-25", note: "" });
  const [sent, setSent] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    // For preview/static use we just compose a mailto and surface a success state.
    const subject = `OncoCadence demo request — ${form.name} (${form.org || "—"})`;
    const body = `Name: ${form.name}\nRole: ${form.role}\nOrganization: ${form.org}\nEmail: ${form.email}\nPhone: ${form.phone}\nActive chemo patients: ${form.patients}\n\nNotes:\n${form.note}`;
    window.location.href = `mailto:DEMO@BioMedMeet.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSent(true);
  };

  if (sent) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-5" />
        <h1 className="text-3xl font-bold">Request sent.</h1>
        <p className="text-slate-400 mt-3">Your email client just opened with the pre-filled request. Send it, and we&apos;ll respond within one business day with two demo slots.</p>
        <div className="mt-8 glass-card p-6 text-left text-sm">
          <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-2">If nothing happened</div>
          <p className="text-slate-400">Email <a href="mailto:DEMO@BioMedMeet.com" className="text-emerald-400 hover:text-emerald-300">DEMO@BioMedMeet.com</a> directly, or call <a href="tel:+14848026153" className="text-emerald-400 hover:text-emerald-300">+1 (484) 802-6153</a>.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12 pt-16 pb-10 text-center">
        <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-500 font-bold mb-3">Request a demo</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight max-w-3xl mx-auto">30 minutes. Four user roles. One live demo environment.</h1>
        <p className="text-slate-400 mt-4 max-w-2xl mx-auto">Tell us a bit about your oncology practice. We&apos;ll send back two demo slots within one business day and walk through the workflow that matters most for your team.</p>
      </section>

      <section className="max-w-3xl mx-auto px-6 sm:px-8 md:px-12 pb-20">
        <form onSubmit={submit} className="glass-card p-7 md:p-9 space-y-4" data-testid="demo-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Your name"        value={form.name}  onChange={(v) => setForm({ ...form, name: v })}  required testid="demo-name" />
            <FieldSelect label="Your role"  value={form.role}  onChange={(v) => setForm({ ...form, role: v })}  options={["Doctor","Nurse","Admin","Organizer","Patient advocate","IT / Procurement"]} testid="demo-role" />
          </div>
          <Field label="Organization"     value={form.org}   onChange={(v) => setForm({ ...form, org: v })}   placeholder="e.g. Mercy Cancer Center" testid="demo-org" />
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Work email"     value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" required testid="demo-email" />
            <Field label="Phone"          value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel"  testid="demo-phone" />
          </div>
          <FieldSelect label="Active chemo patients in your practice" value={form.patients} onChange={(v) => setForm({ ...form, patients: v })} options={["1-25","26-100","101-250","251-500","500+"]} testid="demo-patients" />
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold block mb-2">Anything you&apos;d like us to focus on?</label>
            <textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="e.g. AC-T workflow, multi-site rollout, IT security review…" className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 focus:border-emerald-500 focus:outline-none text-slate-100 placeholder:text-slate-500" data-testid="demo-note" />
          </div>
          <button type="submit" className="w-full inline-flex items-center justify-center gap-2 py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-[#0B1220] font-bold transition-colors shadow-lg shadow-emerald-500/20" data-testid="demo-submit">
            Send my demo request <ArrowRight className="w-4 h-4" />
          </button>
          <p className="text-xs text-slate-500 text-center">By submitting, you&apos;ll be redirected to your email client with a pre-filled request to <span className="text-emerald-400">DEMO@BioMedMeet.com</span>.</p>
        </form>

        <div className="mt-8 glass-card p-5 grid sm:grid-cols-2 gap-3 text-sm">
          <a href="mailto:DEMO@BioMedMeet.com" className="flex items-center gap-2 text-slate-300 hover:text-emerald-400" data-testid="demo-fallback-email">
            <Mail className="w-4 h-4 text-emerald-400" />DEMO@BioMedMeet.com
          </a>
          <a href="tel:+14848026153" className="flex items-center gap-2 text-slate-300 hover:text-emerald-400" data-testid="demo-fallback-phone">
            <Phone className="w-4 h-4 text-emerald-400" />+1 (484) 802-6153
          </a>
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", placeholder = "", required = false, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold block mb-2">{label}{required && <span className="text-emerald-400 ml-1">*</span>}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder} className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 focus:border-emerald-500 focus:outline-none text-slate-100 placeholder:text-slate-500" data-testid={testid} />
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold block mb-2">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full px-4 py-3 rounded-lg bg-white/[0.04] border border-white/10 focus:border-emerald-500 focus:outline-none text-slate-100" data-testid={testid}>
        {options.map((o) => <option key={o} value={o} className="bg-[#111827]">{o}</option>)}
      </select>
    </div>
  );
}
